package com.essntl.features.service.data.repository

import com.essntl.core.supabase.*
import com.essntl.core.utils.repository.Repository
import com.essntl.features.service.data.supabase.extraservice.ExtraServiceDto
import com.essntl.features.service.data.supabase.extraservice.ExtraServiceSupabaseDataSource
import com.essntl.features.service.data.supabase.extraservice.toRequestMap
import com.essntl.features.service.data.supabase.service.ServiceDto
import com.essntl.features.service.data.supabase.service.ServiceSupabaseDataSource
import com.essntl.features.service.data.supabase.service.toRequestMap
import com.essntl.features.service.data.supabase.supplierservice.SupplierServiceDto
import com.essntl.features.service.data.supabase.supplierservice.SupplierServiceSupabaseDataSource
import com.essntl.features.service.data.supabase.supplierservice.toRequestMap
import com.essntl.features.service.domain.model.extraservice.ExtraServiceModel
import com.essntl.features.service.domain.model.service.ServiceFilterOptions
import com.essntl.features.service.domain.model.service.ServiceModel
import com.essntl.features.service.domain.model.service.ServiceOrderType
import com.essntl.features.service.domain.model.supplierservice.SupplierServiceModel
import com.essntl.features.service.domain.repository.ServiceRepository
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.PostgrestFilterBuilder
import org.koin.core.annotation.Single

@Single(binds = [ServiceRepository::class])
internal class ServiceRepositoryImpl internal constructor(
    private val serviceDataSource: ServiceSupabaseDataSource,
    private val supplierServiceDataSource: SupplierServiceSupabaseDataSource,
    private val extraServiceDataSource: ExtraServiceSupabaseDataSource,
) : Repository(), ServiceRepository {
    override suspend fun get(id: String): Result<ServiceModel> =
        runCatching {
            val service =
                serviceDataSource.getById(
                    id = id,
                    columns = ServiceDto.getColumns(),
                )

            service.toModel()
        }

    override suspend fun getCountServicesByCategory(categoryId: String): Result<Long> =
        runCatching {
            serviceDataSource.getCount {
                filter {
                    eq("category", categoryId)
                }
            }
        }

    override suspend fun getServicesByImage(imageId: String): Result<List<ServiceModel>> =
        runCatching {
            serviceDataSource.getAll(
                columns = ServiceDto.getColumns(),
            ) {
                filter {
                    contains("images", listOf(imageId))
                }
            }.map { it.toModel() }
        }

    override suspend fun getByIds(ids: List<String>): Result<List<ServiceModel>> =
        runCatching {
            val serviceList =
                serviceDataSource.getByIds(
                    ids = ids,
                    columns = ServiceDto.getColumns(),
                )

            serviceList
                .map { service ->
                    service.toModel()
                }
        }

    override suspend fun getBySupplierId(id: String): Result<List<ServiceModel>> =
        runCatching {
            val supplierServiceList =
                supplierServiceDataSource.getAll(
                    columns = SupplierServiceDto.getColumns(),
                ) {
                    filter {
                        eq("supplier_id", id)
                    }
                }

            supplierServiceList
                .map { it.service.toModel() }
        }

    override suspend fun getServiceSuppliers(serviceId: String): Result<List<SupplierServiceModel>> =
        runCatching {
            val supplierServiceList =
                supplierServiceDataSource.getAll(
                    columns = SupplierServiceDto.getColumns(),
                ) {
                    filter {
                        eq("service_id", serviceId)
                    }
                }

            supplierServiceList.map { service ->
                service.toModel()
            }
        }

    override suspend fun getServicesByOrganization(organizationId: String): Result<List<ServiceModel>> =
        runCatching {
            val supplierServiceList =
                supplierServiceDataSource.getAll(
                    columns = SupplierServiceDto.getColumns(),
                ) {
                    filter {
                        eq("organization_id", organizationId)
                    }
                }

            supplierServiceList.map { supplierService ->
                supplierService
                    .service
                    .toModel()
            }
        }

    override suspend fun getAll(
        page: Long,
        limit: Int,
        filterOptions: ServiceFilterOptions,
    ): Result<List<ServiceModel>> =
        runCatching {
            val serviceList =
                serviceDataSource.getAll(
                    page = page,
                    limit = limit,
                    columns = ServiceDto.getColumns(),
                ) {
                    if (filterOptions.isFilterApplied) {
                        filter {
                            if (filterOptions.searchText.isNotEmpty()) {
                                or {
                                    ilike("title", "%${filterOptions.searchText}%")
                                    ilike("headline", "%${filterOptions.searchText}%")
                                    ilike("reference_code", "%${filterOptions.searchText}%")
                                }
                            }
    
                            and {
                                applyCategoryTagsAreaSupplier(filterOptions)
                            }
                        }
                    }

                    when (filterOptions.selectedServiceOrderType) {
                        ServiceOrderType.Alphabetical ->
                            order(
                                column = "title",
                                order = Order.ASCENDING,
                            )

                        ServiceOrderType.LastCreated ->
                            order(
                                column = "created_at",
                                order = Order.DESCENDING,
                            )

                        ServiceOrderType.RecentlyChanged ->
                            order(
                                column = "updated_at",
                                order = Order.DESCENDING,
                            )
                    }
                }

            serviceList
                .map { service ->
                    service.toModel()
                }
        }
            .onFailure {
                it.printStackTrace()
            }

    private fun PostgrestFilterBuilder.applyCategoryTagsAreaSupplier(filterOptions: ServiceFilterOptions) {
        if (filterOptions.selectedCategoryIds.isNotEmpty()) {
            or {
                filterOptions.selectedCategoryIds.forEach { categoryId ->
                    eq("category", categoryId)
                }
            }
        }

        filterOptions.excludeCategoryId?.let { id ->
            if (id.isNotEmpty())
                neq("category", id)
        }

        if (filterOptions.selectedTagIds.isNotEmpty()) {
            contains("tags", filterOptions.selectedTagIds.toList())
        }

        if (filterOptions.selectedAreaIds.isNotEmpty()) {
            or {
                filterOptions.selectedAreaIds.forEach { areaId ->
                    eq("area_id", areaId)
                }
            }
        }

        filterOptions.supplier?.let { supplier ->
            contains("suppliers", listOf(supplier.id))
        }
    }

    override suspend fun getCount(
        filterOptions: ServiceFilterOptions,
    ): Result<Long> =
        runCatching {
            serviceDataSource.getCount {
                if (filterOptions.isFilterApplied) {
                    filter {
                        if (filterOptions.searchText.isNotEmpty()) {
                            or {
                                ilike("title", "%${filterOptions.searchText}%")
                                ilike("headline", "%${filterOptions.searchText}%")
                                ilike("reference_code", "%${filterOptions.searchText}%")
                            }
                        }

                        applyCategoryTagsAreaSupplier(filterOptions)
                    }
                }
            }
        }

    override suspend fun insert(model: ServiceModel): Result<ServiceModel> =
        runCatching {
            val requestMap = model.toRequestMap()
            serviceDataSource
                .insert(
                    requestMap = requestMap,
                    columns = ServiceDto.getColumns(),
                ).toModel()
        }

    override suspend fun insertSupplierService(model: SupplierServiceModel): Result<SupplierServiceModel> =
        runCatching {
            val requestMap = model.toRequestMap()
            supplierServiceDataSource
                .insert(
                    requestMap = requestMap,
                    columns = SupplierServiceDto.getColumns(),
                ).toModel()
        }

    override suspend fun insertExtraService(model: ExtraServiceModel): Result<ExtraServiceModel> =
        runCatching {
            extraServiceDataSource
                .insert(
                    requestMap = model.toRequestMap(),
                    columns = ExtraServiceDto.getColumns(),
                )
                .toModel()
        }

    override suspend fun getExtraServicesByIds(ids: List<String>): Result<List<ExtraServiceModel>> =
        runCatching {
            val extraServiceList =
                extraServiceDataSource.getByIds(
                    ids = ids,
                    columns = ExtraServiceDto.getColumns(),
                )

            extraServiceList
                .map { service ->
                    service.toModel()
                }
        }

    override suspend fun update(model: ServiceModel): Result<ServiceModel> =
        runCatching {
            val requestMap = model.toRequestMap()
            serviceDataSource
                .update(
                    id = model.id,
                    requestMap = requestMap,
                    columns = ServiceDto.getColumns(),
                ).toModel()
        }

    override suspend fun delete(model: ServiceModel): Result<Unit> =
        runCatching {
            serviceDataSource.delete(id = model.id)
        }

    override suspend fun deleteSupplierService(model: SupplierServiceModel): Result<Unit> =
        runCatching {
            supplierServiceDataSource.delete(id = model.id)
        }
}
